/**
 * analyseerWeek — PURE functie: rekent een GEGEVEN week-rooster DOOR zonder te
 * genereren (meet en kijkt na wat er al staat). Basis voor "handmatig wijzigen"
 * (balans + waarschuwingen live bijwerken).
 *
 * Invoer: weekRooster = de geneste engine-structuur rooster[gemKey][dienstId][dag] = namen[].
 * Eén bron van waarheid: alle reken-/regel-logica wordt IMPORTEERD uit rooster-engine.js
 * (isBeschikbaar, schoolweekType, schoolTeltAlsWerk, berekenVasteBezeteUren,
 * berekenBezettingsDoel, controleerCAOGrenzen) — geen gekopieerde engine-logica.
 *
 * Levert:
 *   urenTeller        { mwKey: uren }  — zelfde grondslag als de engine
 *   conflicten        onderbezetting per gemeente/dienst/dag (WIJKT af van Stap 6: afwezigen
 *                     tellen hier niet als bezetting — zie de toelichting bij de telling)
 *   caoOvertredingen  urenTeller > weekcap (zelfde als controleerCAOGrenzen)
 *   rustSignalen      late op dag d + dagdienst op d+1 (TONEN, niet blokkeren)
 *   dubbeleSignalen   zelfde persoon 2x op een dag
 *   gemeenteSignalen  persoon op dienst/gemeente/dag waar hij niet hoort
 *
 * Daarnaast staat hier bepaalReeksSignalen — bewust een APARTE export en geen uitbreiding van
 * analyseerWeek: een reeks aaneengesloten dagen loopt over weekgrenzen, en analyseerWeek ziet
 * per aanroep maar één week. Zie de toelichting bij die functie.
 */
import {
  isBeschikbaar,
  schoolweekType,
  berekenVasteBezeteUren,
  berekenBezettingsDoel,
  controleerCAOGrenzen,
  isGewerkteDag,
  isTeamleiderOnly,
  MAX_AANEENGESLOTEN_WERKDAGEN,
} from './rooster-engine.js';

const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

// — dienst-dagen voor een weektype: exact dezelfde keuze als de engine bij slot-opbouw
//   (rooster-engine.js:500-505) —
function dienstDagenVoorType(dienst, weekType) {
  if (weekType === 'A' && dienst.week_a_dagen) return dienst.week_a_dagen;
  if (weekType === 'B' && dienst.week_b_dagen) return dienst.week_b_dagen;
  if (dienst.week_a_dagen || dienst.week_b_dagen) return [];
  return dienst.dagen || [];
}

// — afwezig (verlof/ziekte/anders) op deze dag? —
//
// SLEUTEL IS DE NAAM, niet mwKey. bouwAfwezigheidMap vult `result[item.naam]`
// (data/afwezigheid.js:127) en isBeschikbaar leest hem net zo op (rooster-engine.js:26). Het
// rooster draagt óók namen, dus dit is een directe lookup zonder omweg via naamNaarKey.
//
// De Set/array-terugval is EXACT de vorm die isBeschikbaar gebruikt (rooster-engine.js:27) en
// die verlof-check.js:35-39 al een keer apart heeft gezet: bouwAfwezigheidMap levert een Set,
// een oudere of handmatig gebouwde map kan een array zijn. Die private helper is hier niet
// importeerbaar (verlof-check.js exporteert alleen checkVerlof), dus dezelfde vorm staat hier —
// wijzigt er één, dan horen ze alle drie mee.
//
// ALLE typen tellen als afwezig. De map draagt het type niet eens: bouwAfwezigheidMap bewaart
// per naam alleen de DAGEN. Wie het type nodig heeft gebruikt bouwAfwezigheidTypeMap.
function isAfwezigOpDag(afwezigheid, naam, dag) {
  const afw = afwezigheid[naam];
  if (!afw) return false;
  return afw instanceof Set ? afw.has(dag) : afw.includes(dag);
}

export function analyseerWeek(weekRooster, regels, weeknummer, isoJaar = null, weekType = null, opties = {}) {
  const afwezigheid = opties.afwezigheid || {};
  const weekCaps = opties.weekCaps || null; // optioneel: zonder caps geldt contract_uren (= controleerCAOGrenzen-grondslag)
  const vakantieDagen = opties.vakantieDagen || null; // Set<dag-code> | null — spiegelt rooster-engine.js:516 (dagdienst-skip in vakantie)
  const koppelGrootte = regels.instellingen?.koppel_grootte || 2;
  const type = weekType ?? schoolweekType(weeknummer);

  const mwKeys = Object.keys(regels.medewerkers).filter(k => !k.startsWith('_'));
  const naamNaarKey = {};
  for (const k of mwKeys) naamNaarKey[regels.medewerkers[k].naam] = k;

  // ── (a) urenTeller: vaste bezette uren + 8u per straat-dienst-plaatsing ──────
  const urenTeller = {};
  for (const k of mwKeys) urenTeller[k] = berekenVasteBezeteUren(regels.medewerkers[k], type, null, vakantieDagen);
  for (const gemKey of Object.keys(weekRooster)) {
    for (const dienstId of Object.keys(weekRooster[gemKey])) {
      for (const dag of Object.keys(weekRooster[gemKey][dienstId])) {
        for (const naam of weekRooster[gemKey][dienstId][dag]) {
          const k = naamNaarKey[naam];
          if (k) urenTeller[k] += 8;
        }
      }
    }
  }

  // ── (b) onderbezetting: reconstrueer de slots zoals de engine (Stap 6) ───────
  const conflicten = [];
  const gemeenten = Object.entries(regels.gemeenten)
    .filter(([gk, g]) => !gk.startsWith('_') && g.actief !== false);
  for (const [gemKey, gemeente] of gemeenten) {
    for (const [dienstId, dienst] of Object.entries(gemeente.diensten)) {
      for (const dag of dienstDagenVoorType(dienst, type)) {
        // de engine maakt voor dagdienst-weekend GEEN slot
        if (dienstId === 'dagdienst' && (dag === 'za' || dag === 'zo')) continue;
        // Vakantiestand: de engine bouwt geen dagdienst-slot op een vakantiedag (rooster-engine.js:516)
        // → hier net zo overslaan, anders fantoom-onderbezetting. Late-slots blijven WEL gecheckt.
        if (dienstId === 'dagdienst' && vakantieDagen?.has(dag)) continue;
        const ingepland = weekRooster[gemKey]?.[dienstId]?.[dag] || [];
        // Onderbezet = minder AANWEZIGE personen dan het bezettingsdoel. Het conflict
        // rapporteert in PERSONEN, dezelfde eenheid als het doel, zodat melding en
        // bezettingsdoel niet uiteen kunnen lopen.
        //
        // ⚠️ LOOPT HIER BEWUST UITEEN MET rooster-engine.js Stap 6 (r.1384). Die telt
        // `ingepland.length` — puur het aantal NAMEN in de cel — en BLIJFT dat doen; hij is
        // niet meegewijzigd. Dat is geen vergeetpunt maar het verschil in moment: Stap 6 draait
        // TIJDENS de generatie, waar isBeschikbaar de afwezigen sowieso niet plaatst, dus daar
        // valt niets af te trekken. analyseerWeek draait ERNA, op een rooster waarin verlof of
        // ziekte inmiddels is ingevoerd zonder dat er opnieuw gegenereerd is (of kán worden:
        // een vastgelegde maand blokkeert dat). Twee zieken in een cel telden dan als volle
        // bezetting en de melding bleef stil — di 4 aug 2026, Arnhem late_dienst_dw.
        //
        // ALLEEN ECHTE AFWEZIGHEID telt af: verlof/ziekte/anders, dus wat er in de
        // afwezigheidsmap staat. NIET vrije dag / kantoor / school / andere gemeente. Die maken
        // isBeschikbaar óók false, maar dat is een PLAATSINGSfout — daar gaan de
        // gemeenteSignalen verderop over — en geen gat in de bezetting.
        const afwezigen = ingepland.filter((naam) => isAfwezigOpDag(afwezigheid, naam, dag));
        const bezet = ingepland.length - afwezigen.length;
        const nodig = berekenBezettingsDoel(dienst, false, koppelGrootte);
        if (bezet < nodig) {
          conflicten.push({
            gemeente: gemeente.naam, gemeenteKey: gemKey, dienst: dienstId, dag,
            bezet, nodig,
            // `ingepland` blijft de VOLLEDIGE lijst, ongefilterd: de matrix toont die namen ook,
            // en een ingekorte lijst zou de teamleider naar een cel laten zoeken die er anders
            // uitziet dan de melding beweert. VerlofModal.jsx:119 rekent er bovendien mee.
            ingepland: [...ingepland],
            // De afwezigen los ernaast, ALLEEN de namen: het type zit niet in de map die deze
            // functie krijgt (bouwAfwezigheidMap bewaart per naam alleen de dagen). De renderlaag
            // zet het type erbij — Weekrooster.jsx, conflictenMetDatum — net zoals hij daar de
            // kalenderdatum bijzet. De engine blijft van kalender en labels af.
            afwezigen: [...afwezigen],
            // Op de NIEUWE bezet en niet op ingepland.length. Een cel met twee afwezigen is
            // 0 personen op straat, en dat hoort ROOD te zijn — niet oranje omdat er toevallig
            // twee namen staan. Rood = 0 of 1 aanwezig; oranje = 2+ aanwezig maar nog te weinig.
            ernst: bezet <= 1 ? 'rood' : 'oranje',
            weeknummer, weekType: type,
          });
        }
      }
    }
  }

  // hulpfuncties: late/dag op een dag (zelfde semantiek als heeftLateDienstOp/heeftDagdienstOp)
  const heeftLateOp = (naam, dag) => Object.values(weekRooster).some(gr =>
    Object.entries(gr).some(([id, dm]) => id.startsWith('late_dienst') && Array.isArray(dm[dag]) && dm[dag].includes(naam)));
  const heeftDagOp = (naam, dag) => Object.values(weekRooster).some(gr =>
    Object.entries(gr).some(([id, dm]) => id === 'dagdienst' && Array.isArray(dm[dag]) && dm[dag].includes(naam)));

  // ── (c) regel-signalen (TONEN, niet blokkeren) ──────────────────────────────
  const rustSignalen = [];
  const dubbeleSignalen = [];
  const gemeenteSignalen = [];

  // rust: late op dag d + dagdienst op dag d+1
  for (const k of mwKeys) {
    const naam = regels.medewerkers[k].naam;
    for (let d = 0; d < DAGEN.length - 1; d++) {
      if (heeftLateOp(naam, DAGEN[d]) && heeftDagOp(naam, DAGEN[d + 1])) {
        rustSignalen.push({ medewerker: naam, mwKey: k, laatDag: DAGEN[d], dagDag: DAGEN[d + 1] });
      }
    }
  }

  // dubbel: zelfde persoon meer dan 1x op een dag (over alle gemeenten/diensten)
  const perNaamDag = {};
  for (const gemKey of Object.keys(weekRooster))
    for (const dienstId of Object.keys(weekRooster[gemKey]))
      for (const dag of Object.keys(weekRooster[gemKey][dienstId]))
        for (const naam of weekRooster[gemKey][dienstId][dag])
          (perNaamDag[`${naam}|${dag}`] ||= []).push({ gemKey, dienstId });
  for (const [sl, plaatsingen] of Object.entries(perNaamDag)) {
    if (plaatsingen.length > 1) {
      const [naam, dag] = sl.split('|');
      dubbeleSignalen.push({ medewerker: naam, dag, plaatsingen });
    }
  }

  // beschikbaarheid/gemeente/dag-set/dienst-type — "hoort hier niet"
  // isBeschikbaar wordt met weeknummer=null aangeroepen: net als de engine bij noodvulling
  // (relaxWeekend) telt de weekend-rotatie-OFF-blokkade NIET als harde overtreding; alle
  // harde, weeknummer-onafhankelijke regels (vrije dagen, kantoor, school, werkdagen,
  // gemeente-lidmaatschap, weekend-exclusiviteit) blijven wel gelden.
  for (const [gemKey, gemeente] of Object.entries(regels.gemeenten)) {
    if (gemKey.startsWith('_')) continue;
    const gr = weekRooster[gemKey];
    if (!gr) continue;
    for (const [dienstId, dm] of Object.entries(gr)) {
      const dienst = gemeente.diensten?.[dienstId];
      for (const dag of Object.keys(dm)) {
        for (const naam of dm[dag]) {
          const redenen = [];
          const k = naamNaarKey[naam];
          if (!k) {
            gemeenteSignalen.push({ medewerker: naam, gemeenteKey: gemKey, dienst: dienstId, dag, redenen: ['onbekende medewerker'] });
            continue;
          }
          const mw = regels.medewerkers[k];
          if (gemeente.actief === false) redenen.push('gemeente inactief');
          if (!dienst) {
            redenen.push('onbekende dienst');
          } else {
            const dagen = dienstDagenVoorType(dienst, type);
            if (!dagen.includes(dag)) {
              // Lingewaard vakantie-late-conversie: de engine vervangt in een vakantieweek de onderdrukte
              // dagdienst (di/do) door een late op diezelfde dag (rooster-engine.js:518-529). Die dag staat
              // bewust niet in de late-dienst-dagen; geef daarvoor GEEN "dag niet in dienst-dagen"-signaal.
              // Exact dezelfde gate als de engine: lingewaard + late + vakantiedag + dag is een dagdienst-dag.
              const isVakantieLateConversie =
                gemKey === 'lingewaard' &&
                dienstId.startsWith('late_dienst') &&
                vakantieDagen?.has(dag) &&
                (dienstDagenVoorType(gemeente.diensten?.dagdienst, type) || []).includes(dag);
              if (!isVakantieLateConversie) {
                redenen.push(`dag '${dag}' niet in dienst-dagen [${dagen.join(',')}]`);
              }
            }
          }
          if (!isBeschikbaar(k, mw, dag, gemKey, afwezigheid, type, null, vakantieDagen)) redenen.push('niet beschikbaar (isBeschikbaar=false)');
          // dienst-type eligibility: dezelfde filters die vulSlot toepast
          if (dienstId === 'dagdienst' && mw.geen_dagdienst) redenen.push('geen_dagdienst op dagdienst');
          if (dienstId === 'dagdienst' && mw.geen_dagdienst_dagen?.includes(dag)) redenen.push(`geen_dagdienst op '${dag}'`);
          if (dienstId.startsWith('late_dienst') || dienstId === 'flexibel') {
            if (mw.alleen_dagdienst && dag !== 'za' && dag !== 'zo') redenen.push('alleen_dagdienst op late/flex (weekdag)');
            if (mw.geen_late_dienst_dagen?.includes(dag)) redenen.push(`geen_late_dienst op '${dag}'`);
          }
          if (redenen.length) gemeenteSignalen.push({ medewerker: naam, mwKey: k, gemeenteKey: gemKey, dienst: dienstId, dag, redenen });
        }
      }
    }
  }

  // ── (d) CAO ──────────────────────────────────────────────────────────────────
  const caoOvertredingen = controleerCAOGrenzen(urenTeller, regels.medewerkers, weekCaps);

  return { urenTeller, conflicten, caoOvertredingen, rustSignalen, dubbeleSignalen, gemeenteSignalen, weekType: type, weeknummer, isoJaar };
}

/**
 * bepaalReeksSignalen — wie werkt er MEER dan de toegestane aaneengesloten dagen achter elkaar?
 *
 * Waarom naast analyseerWeek en niet erin: een reeks loopt over weekgrenzen (ma van de ene week
 * sluit aan op zo van de vorige), en analyseerWeek krijgt per aanroep precies één week. Van de
 * negen reeksen die de scan op de live-data vindt ligt er één volledig binnen één week; de rest
 * zou een week-gebonden check nooit zien. Deze functie krijgt daarom de hele wekenreeks in één
 * keer. analyseerWeek blijft ongewijzigd, dus geen van zijn call-sites verandert.
 *
 * `weken`  chronologisch geordende, AANEENGESLOTEN week-objecten (weeknummer + rooster, en bij
 *          voorkeur schoolweekType — isGewerkteDag valt anders terug op het weeknummer).
 *          Aaneengesloten is een eis aan de aanroeper: een gat zou twee losse reeksen als één
 *          tellen. Ontbreekt de week ervóór, dan begint de teller blanco bij de eerste week —
 *          dezelfde blinde start die de blok-generator logt (blok-generator.js:101-107).
 * `regels` voor de medewerkers en voor instellingen.max_aaneengesloten_werkdagen.
 * `opties` { afwMaps, vakantieMaps } — beide op WEEKNUMMER gesleuteld, net als elders in de
 *          engine. Ontbreekt een week, dan telt die zonder afwezigheid/vakantiedagen.
 *
 * "Gewerkt" is niet opnieuw gedefinieerd: dat is isGewerkteDag (rooster-engine.js:197), dezelfde
 * bron waarmee de reeks-regel tijdens generatie wordt getoetst. Teamleider-only wordt overgeslagen
 * — die staat nooit in het rooster, maar zou via de kantoor-/schooltak wel een fantoomreeks geven.
 *
 * -> [{ medewerker, mwKey, lengte, vanWeek, vanDag, totWeek, totDag }], puur lezend.
 * Bewust GEEN datums of teksten: dat is locale-werk en hoort in de laag die het toont.
 */
export function bepaalReeksSignalen(weken, regels, opties = {}) {
  const afwMaps = opties.afwMaps || {};
  const vakantieMaps = opties.vakantieMaps || {};
  const grens = regels?.instellingen?.max_aaneengesloten_werkdagen ?? MAX_AANEENGESLOTEN_WERKDAGEN;

  const rij = (weken || []).filter(w => w && w.rooster);
  if (!rij.length || !regels?.medewerkers) return [];

  const signalen = [];
  for (const [mwKey, mw] of Object.entries(regels.medewerkers)) {
    if (mwKey.startsWith('_') || typeof mw !== 'object' || mw === null) continue;
    if (isTeamleiderOnly(mw)) continue;

    let lopend = [];
    const sluit = () => {
      if (lopend.length > grens) {
        const eerste = lopend[0];
        const laatste = lopend[lopend.length - 1];
        signalen.push({
          medewerker: mw.naam,
          mwKey,
          lengte: lopend.length,
          vanWeek: eerste.weeknummer,
          vanDag: eerste.dag,
          totWeek: laatste.weeknummer,
          totDag: laatste.dag,
        });
      }
      lopend = [];
    };

    for (const week of rij) {
      for (const dag of DAGEN) {
        if (isGewerkteDag(mw, dag, week, afwMaps[week.weeknummer] || null, vakantieMaps[week.weeknummer] || null)) {
          lopend.push({ weeknummer: week.weeknummer, dag });
        } else {
          sluit();
        }
      }
    }
    sluit();
  }
  return signalen;
}
